if (!customElements.get('subscription-options')) {
  class SubscriptionOptions extends ObservedElement {
    quantityUpdateUnsubscriber = undefined;

    constructor() {
      super();

      this.savingsContainer = this.querySelector('[data-savings]');
      this.purchaseOptions = this.querySelector('[data-purchase-options]');
      this.subscriptions = this.querySelector('[data-subscription-options]');
      const section = this.closest('.shopify-section');
      this.mainPrice = section.querySelector(
        `#price-${this.dataset.section} .price`,
      );
      this.replacementContainers = {
        savings: section.querySelectorAll('[data-subscription-savings]'),
        summary: section.querySelectorAll('[data-subscription-summary]'),
        price: section.querySelectorAll('[data-variant-price]'),
        purchaseType: section.querySelectorAll('[data-purchase-type]'),
      };
      this.relativeURL = `${this.dataset.url}${window.location.search}`;
      this.quantity = 1;

      this.addListener(this.purchaseOptions, 'change', this.setPurchaseOption);
      this.addListener(this.subscriptions, 'change', this.setSubscription);

      this.addAttributeListener('variant-id', this.setVariant);
      this.addAttributeListener('selling-plan-id', this.setSellingPlan);
    }

    defaults() {
      return {
        productType: 'bottle',
        productTypePlural: 'bottles',
        discount: 10,
        purchaseType: 'subscription',
        price: null,
        productId: null,
        variantId: null,
        sellingPlanId: null,
      };
    }

    static get observedAttributes() {
      return ['purchase-type', 'price', 'variant-id', 'selling-plan-id'];
    }

    connectedCallback() {
      super.connectedCallback();
      this.quantityUpdateUnsubscriber = subscribe(
        PUB_SUB_EVENTS.quantityUpdate,

        e => {
          const old = this.quantity;
          this.quantity = Number(e.data.value);
          if (this.quantity !== old) {
            this.updateSubscriptionInformation();
          }
        },
      );
    }

    disconnectedCallback() {
      super.disconnectedCallback();
      if (this.quantityUpdateUnsubscriber) {
        this.quantityUpdateUnsubscriber();
      }
    }

    init() {
      super.init();

      this.setSellingPlan(this.options.sellingPlanId);
      this.setVariant(this.options.variantId);
    }

    getVariantData() {
      this.variantData =
        this.variantData ||
        JSON.parse(this.querySelector('[data-variants]').textContent);
      return this.variantData;
    }

    getSellingPlanData() {
      this.sellingPlanData =
        this.sellingPlanData ||
        JSON.parse(this.querySelector('[data-selling-plans]').textContent);
      return this.sellingPlanData;
    }

    setVariant(id) {
      if (!this.initialized) return;

      this.currentVariant = this.getVariantData().find(
        variant => variant.id == id,
      );

      if (!this.currentVariant) return;

      this.updateURL();
      this.updateVariantInput();
      this.updateShareUrl();
      this.updateSubscriptionInformation();

      this.publishVariantChange();
    }

    setSellingPlan(id) {
      if (!this.initialized) return;

      this.currentSellingPlan = this.getSellingPlanData().find(
        sellingPlan => sellingPlan.id == id,
      );

      if (this.currentSellingPlan) {
        const spName = this.currentSellingPlan.name.toLowerCase();
        if (spName.includes('every 1 month')) {
          this.currentInterval = 1;
        } else if (spName.includes('every 3 months')) {
          this.currentInterval = 3;
        } else {
          console.error(
            `Selling plan name not recognized: ${spName}`,
            this.currentSellingPlan,
          );
        }
      } else {
        this.currentInterval = null;
      }

      this.currentSavings =
        this.options.discount + (this.currentInterval > 1 ? 5 : 0);

      this.updateSellingPlanInput();
    }

    publishVariantChange() {
      const sectionId = this.dataset.section;
      const html = new DOMParser().parseFromString(
        document.getElementById(`shopify-section-${this.dataset.section}`)
          .outerHTML,
        'text/html',
      );
      const productId = this.options.productId;
      const variant = this.currentVariant;

      publish(PUB_SUB_EVENTS.variantChange, {
        data: { sectionId, html, productId, variant },
      });
    }

    setPurchaseOption(e) {
      const purchaseType = e.target.value;

      this.setAttribute('purchase-type', purchaseType);

      let variantId, sellingPlanId;
      if (purchaseType === 'one-time') {
        variantId = e.target.dataset.variantId;
        sellingPlanId = null;
      } else {
        const currentSubscriptionInput =
          this.subscriptions.querySelector('input');
        currentSubscriptionInput.click();
        variantId = currentSubscriptionInput.dataset.variantId;
        sellingPlanId = currentSubscriptionInput.value;
      }

      this.setAttribute('selling-plan-id', sellingPlanId);
      this.setAttribute('variant-id', variantId);
    }

    setSubscription(e) {
      this.setAttribute('selling-plan-id', e.target.value);
      this.setAttribute('variant-id', e.target.dataset.variantId);
    }

    updateURL() {
      if (this.dataset.updateUrl === 'false') return;
      const params = new URLSearchParams(window.location.search);
      params.set('variant', this.currentVariant.id);

      this.relativeURL = `${this.dataset.url}?${params.toString()}`;

      window.history.replaceState({}, '', this.relativeURL);
    }

    getPrices(variant, discount) {
      const multiplier = 1 - discount / 100;
      return {
        price: Math.round(variant.price * multiplier),
        compareAtPrice:
          variant.compare_at_price > variant.price
            ? Math.round(variant.compare_at_price * multiplier)
            : 0,
      };
    }

    formatPrice(price, includeCurrency = true) {
      const currency =
        includeCurrency && this.dataset.currencyCodeEnabled === 'true'
          ? ` ${Shopify.currency.active}`
          : '';
      return Shopify.formatMoney(price) + currency;
    }

    getPriceMarkup({ price, compareAtPrice }, includeCurrency = true) {
      const container = document.createElement('span');
      const onSale = compareAtPrice > price;
      const addLabel = label => {
        const span = document.createElement('span');
        span.className = 'visually-hidden visually-hidden--inline';
        span.textContent = label;
        container.append(span);
      };

      addLabel(this.dataset.regularPriceLabel);
      if (onSale) {
        const compare = document.createElement('s');
        compare.className = 'price-item price-item--regular';
        compare.textContent = this.formatPrice(compareAtPrice, includeCurrency);
        container.append(compare, ' ');
        addLabel(this.dataset.salePriceLabel);
      }
      const current = document.createElement('span');
      current.className = `price-item price-item--${onSale ? 'sale' : 'regular'}`;
      current.textContent = this.formatPrice(price, includeCurrency);
      container.append(current);
      return container.innerHTML;
    }

    updateMainPrice({ price, compareAtPrice }) {
      if (!this.mainPrice) return;

      this.mainPrice.classList.toggle('price--on-sale', compareAtPrice > price);
      this.mainPrice
        .querySelectorAll(
          '.price__regular .price-item--regular, .price__sale .price-item--sale',
        )
        .forEach(element => {
          element.innerHTML = this.formatPrice(price);
        });
      const compare = this.mainPrice.querySelector('.price__sale s');
      if (compare) compare.innerHTML = this.formatPrice(compareAtPrice);
    }

    updateSubscriptionInformation() {
      if (!this.currentVariant) return;

      const discount = this.currentSellingPlan ? this.options.discount : 0;
      const prices = this.getPrices(this.currentVariant, discount);
      this.setAttribute('price', (prices.price / 100).toFixed(2));
      this.updateMainPrice(prices);
      const subscriptionInput = this.currentSellingPlan
        ? this.subscriptions.querySelector('input:checked')
        : this.subscriptions.querySelector('input');
      const subscriptionVariant =
        this.getVariantData().find(
          variant => variant.id == subscriptionInput?.dataset.variantId,
        ) || this.currentVariant;
      const subscriptionPrices = this.getPrices(
        subscriptionVariant,
        this.options.discount,
      );
      const qty = this.quantity * this.currentInterval;

      const replacements = {
        purchaseType:
          this.options.purchaseType === 'one-time'
            ? window.subscriptionStrings.oneTimePurchase
            : window.subscriptionStrings.subscriptionPurchase,
        savings: {
          discount: subscriptionInput?.dataset.discount || this.currentSavings,
          price: this.getPriceMarkup(subscriptionPrices, false),
        },
        summary: {
          qty: qty,
          count: this.currentInterval,
          type:
            qty > 1 ? this.options.productTypePlural : this.options.productType,
        },
        price: this.getPriceMarkup(prices),
      };

      for (const [key, elements] of Object.entries(
        this.replacementContainers,
      )) {
        const replacementPattern = new RegExp(
          Object.keys(replacements[key])
            .map(k => `\\[${k}\\]`)
            .join('|'),
          'gi',
        );

        let localeString = window.subscriptionStrings[key];
        if (typeof window.subscriptionStrings[key] === 'object') {
          localeString =
            localeString[replacements[key].count > 1 ? 'other' : 'one'];
        }

        const replacementString =
          localeString?.replace(
            replacementPattern,
            match => replacements[key][match.replace(/[\[\]]/g, '')],
          ) ?? replacements[key];

        for (const element of elements) {
          element.innerHTML = replacementString;
        }
      }
    }

    updateVariantInput() {
      const productForms = document.querySelectorAll(
        `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
      );

      productForms.forEach(productForm => {
        const input = productForm.querySelector('input[name="id"]');

        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updateSellingPlanInput() {
      const productForms = document.querySelectorAll(
        `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
      );

      productForms.forEach(productForm => {
        const input = productForm.querySelector('input[name="selling_plan"]');

        input.value = this.currentSellingPlan?.id ?? null;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    updateShareUrl() {
      const shareButton = document.getElementById(
        `Share-${this.dataset.section}`,
      );
      if (!shareButton?.updateUrl) return;

      shareButton.updateUrl(
        `${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}&share=true`,
      );
    }
  }

  customElements.define('subscription-options', SubscriptionOptions);
}
